// =============================================================================
// io/sync/accountDeletion.ts — schedule, read and cancel a pending deletion.
// -----------------------------------------------------------------------------
// ── 🚨 1. INSERT, NOT UPSERT — THE TABLE HAS NO UPDATE POLICY ──
//
// `20260711000003_account_deletions.sql` says it outright: *"No update policy —
// a schedule is insert-or-nothing."* There are exactly three policies, for
// select, insert and delete.
//
// The old client uses `.upsert(..., { onConflict: 'user_id' })`. An upsert
// against an existing row performs an UPDATE, which RLS refuses — so scheduling
// a deletion that is ALREADY scheduled fails with a permissions error, on the
// one screen where a confusing error is least welcome.
//
// ⚠ AND IF IT DID WORK IT WOULD BE WRONG. Overwriting `requested_at` restarts
// the thirty-day clock, so a user who pressed delete twice would silently get a
// later purge date than the one they were promised the first time. Insert-only
// keeps the original deadline, which is the honest one.
//
// ── 🚨 2. THE TIMESTAMP COMES FROM THE SERVER, NOT FROM HERE ──
//
// The column defaults to `now()`. This inserts ONLY `user_id` and reads the
// stored value back, so the purge date shown to the user is derived from the
// same instant the purge job will compare against. Sending a browser timestamp
// puts clock skew between the promise and the deletion.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { reportFailureAs } from '../diagnostics/errorLog';

export interface PendingDeletion {
  requestedAt: string;
}

export interface ScheduleResult {
  pending: PendingDeletion | null;
  error: string | null;
  /** True when a schedule already existed — not a failure. */
  alreadyScheduled: boolean;
}

/**
 * Is a deletion pending? `null` means no.
 *
 * ⚠ THROWS on a read failure rather than returning null. "No deletion pending"
 * is a claim, and making it wrongly means the user is never told their account
 * is counting down.
 */
export async function loadPendingDeletion(
  sb: SupabaseClient,
): Promise<PendingDeletion | null> {
  const { data, error } = await sb
    .from('account_deletions')
    .select('requested_at')
    .limit(1);

  if (error) throw new Error(`Couldn’t check your account status: ${error.message}`);
  const row = (data ?? [])[0] as { requested_at?: string } | undefined;
  return row?.requested_at ? { requestedAt: row.requested_at } : null;
}

/** Postgres unique-violation — the row is already there. */
const UNIQUE_VIOLATION = '23505';

export async function scheduleDeletion(
  sb: SupabaseClient, userId: string,
): Promise<ScheduleResult> {
  if (!userId) {
    return { pending: null, error: 'Not signed in.', alreadyScheduled: false };
  }

  // ⚠ `user_id` ONLY. `requested_at` is left to the column default so the stored
  // instant is the server's — see note 2.
  const { data, error } = await sb
    .from('account_deletions')
    .insert({ user_id: userId })
    .select('requested_at');

  if (error) {
    // ⚠ ALREADY SCHEDULED IS NOT A FAILURE. The user asked for the account to be
    // deleted and it is; telling them it went wrong would send them round again.
    // The existing date is read back so the deadline shown is the real one
    // rather than a fresh thirty days.
    if (error.code === UNIQUE_VIOLATION) {
      try {
        const existing = await loadPendingDeletion(sb);
        if (existing) return { pending: existing, error: null, alreadyScheduled: true };
      } catch { /* fall through to the generic message below */ }
    }
    return {
      pending: null,
      error: reportFailureAs(
        'schedule account deletion', error,
        `Could not schedule the deletion: ${error.message}`,
      ),
      alreadyScheduled: false,
    };
  }

  const row = (data ?? [])[0] as { requested_at?: string } | undefined;
  if (!row?.requested_at) {
    // ⚠ The insert reported success and returned nothing. Rather than invent a
    // date, say the state is unknown — this screen must never show a deadline it
    // did not read.
    return {
      pending: null,
      error: 'The deletion was requested but the date could not be read back. '
        + 'Reload this page to check its status before doing anything else.',
      alreadyScheduled: false,
    };
  }
  return { pending: { requestedAt: row.requested_at }, error: null, alreadyScheduled: false };
}

/**
 * Cancel — delete the row.
 *
 * ⚠ VERIFIES A ROW WENT. This is the button that saves someone's account; a
 * "restored" message over a delete that matched nothing leaves them believing
 * they are safe while the clock runs.
 */
export async function restoreAccount(
  sb: SupabaseClient, userId: string,
): Promise<{ error: string | null }> {
  const { data, error } = await sb
    .from('account_deletions')
    .delete()
    .eq('user_id', userId)
    .select('user_id');

  if (error) {
    return {
      error: reportFailureAs(
        'cancel account deletion', error,
        `Could not restore your account: ${error.message}`,
      ),
    };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return {
      error: 'Nothing was cancelled — your account may still be scheduled for '
        + 'deletion. Reload and check before signing out.',
    };
  }
  return { error: null };
}
